import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchInwardDetails, fetchInwardOfficeDetails } from "../fetchHandler";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { Autocomplete, TextField } from "@mui/material";
import { generatePDFInward } from "./inwardPdf";
import LoaderModal from "../../../utils/loader.js/loader";
import { AddButton } from "../../../utils/commonComponent/Custom/Button";
import { DispatchModeOptions } from "../../disciplineMaster/disiplineConst";
import { getPermissions } from "../../rbac/hasAccess";
import InwardTestTableCommom from "./InwardTestTableCommom";
import { fetchUsers } from "../../datasheet/fetchHandler";

const InwardView = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const [letterHead, setLetterHead] = React.useState(true);
  const [electronicSign, setElectronicSign] = React.useState(true);
  const [signImg, setSignImg] = React.useState(false);
  const [userList, setUserList] = useState([]);

  const [inputStates, setInputStates] = useState({});
  const [officeFormData, setOfficeFormData] = useState({});
  const [jobDetails, setJobDetails] = useState({});
  const [testRows, setTestRows] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [isCSRF, setIsCSRF] = useState(false);

  useEffect(() => {
    if (params.id != 0) {
      fetchInwardDetails(params, setInputStates);
      fetchInwardOfficeDetails(params, setOfficeFormData);
    }
  }, [params?.id]);

  const fetchInwardTestRows = async () => {
    try {
      // 1) fetch inward rows
      const inwardTestRows = await fetchQuery(`select * from inwardTests where status = 1 and inwardId = ${params.id}`);

      if (!Array.isArray(inwardTestRows) || inwardTestRows.length === 0) {
        setTestRows([]);
        return;
      }

      // 2) Extract all unique test IDs and material IDs (handling comma-separated values)
      const allTestIds = new Set();
      const materialIds = new Set();
      inwardTestRows.forEach(row => {
        //  Split materialId by comma
        if (row.materialId) {
          String(row.materialId).split(',').map(id => id.trim()).filter(Boolean).forEach(id => materialIds.add(id));
        }
        if (row.testId) {
          String(row.testId).split(',').map(id => id.trim()).filter(Boolean).forEach(id => allTestIds.add(id));
        }
      });

      // 3) fetch test methods for all test IDs
      let testMethod = [];
      if (allTestIds.size > 0) {
        const testIdsQuery = ` and id in (${Array.from(allTestIds).join(",")})`;
        try {
          testMethod = await fetchQuery("select * from materialTestings where status = 1" + testIdsQuery);
        } catch (e) {
          console.warn("Failed to fetch by test IDs, trying material IDs fallback");
        }
      }

      // fallback: if nothing returned, fetch by materialIds
      if ((!Array.isArray(testMethod) || testMethod.length === 0) && materialIds.size > 0) {
        const materialIdsQuery = ` and materialId in (${Array.from(materialIds).join(",")})`;
        testMethod = await fetchQuery("select * from materialTestings where status = 1" + materialIdsQuery);
      }

      // 4) fetch material details
      let materials = [];
      if (materialIds.size > 0) {
        try {
          const materialQuery = ` and id in (${Array.from(materialIds).join(",")})`;
          materials = await fetchQuery("select * from materialScope where status = 1" + materialQuery);
        } catch (e) {
          console.error(`Error fetching material:`, e);
          return null;
        }
      }
      // 5) build lookup maps
      const testMethodMap = (testMethod || []).reduce((acc, t) => {
        acc[String(t.id)] = t;
        return acc;
      }, {});

      const materialDetailsMap = (materials || []).reduce((acc, m) => {
        acc[String(m.id)] = m.MaterialName ?? m.materialName ?? m.name ?? "";
        return acc;
      }, {});

      // 6) compose enhanced rows
      const enhancedInwardTestRows = inwardTestRows.map((row) => {
        const testDetails = {};

        if (row.testId) {
          const testIds = String(row.testId).split(',').map(id => id.trim());
          testIds.forEach(testId => {
            if (testId && testMethodMap[testId]) {
              testDetails[testId] = testMethodMap[testId];
            }
          });
        }

        const resolvedMaterialNames = row.materialId
          ? String(row.materialId)
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean)
              .map((id) => materialDetailsMap[id] ?? "")
              .filter(Boolean)
              .join(",")
          : "";

        return {
          ...row,
          testDetails,
          materialName: resolvedMaterialNames,
          //  Pass full map so component can resolve per-test materialId → name
          materialNamesMap: materialDetailsMap,
          testMethod: Object.values(testDetails)[0]?.testMethod ?? "",
          testName: Object.values(testDetails)[0]?.testName ?? "",
        };
      });

      setTestRows(enhancedInwardTestRows);
    } catch (error) {
      console.error("Error fetching test master data:", error);
    }
  };

  const fetchJobDetails = async () => {
    try {
      const [jobDetailsById] = await fetchQuery(
        `select * from jobRegister where id = ${inputStates?.jobId}`
      );
      setJobDetails(jobDetailsById);
    } catch (error) {
      console.error("Error fetching job details:", error);
    }
  };

  useEffect(() => {
    if (inputStates?.jobId) {
      fetchJobDetails();
    }
    fetchInwardTestRows();
  }, [inputStates?.jobId]);

  useEffect(() => {
    fetchUsers().then((users) => {
      setUserList(users);
    });
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-4 text-sm text-black">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-5">
        {(permissions.pdfDownload || permissions.allAccess) && (
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 w-full lg:w-auto">

            <div className="w-full sm:w-[220px]">
              <Autocomplete
                size="small"
                id="quotation_view_letterhead"
                options={[
                  { id: 1, label: "With Letter Head" },
                  { id: 2, label: "Without Letter Head" },
                ]}
                renderInput={(params) => (
                  <TextField {...params} label="Letter Head" fullWidth />
                )}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue === "With Letter Head" || newInputValue === "") {
                    setLetterHead(true);
                  } else if (newInputValue === "Without Letter Head") {
                    setLetterHead(false);
                  }
                }}
              />
            </div>

            <div className="w-full sm:w-[220px]">
              <Autocomplete
                size="small"
                id="quotation_view__electronicsign"
                options={[
                  { id: 1, label: "With Electronically Signed" },
                  { id: 2, label: "Without Electronically Signed" },
                  { id: 3, label: "Signature Img" },
                ]}
                renderInput={(params) => (
                  <TextField {...params} label="Electronically Signed" fullWidth />
                )}
                onInputChange={(event, newInputValue) => {
                  if (
                    newInputValue === "With Electronically Signed" ||
                    newInputValue === ""
                  ) {
                    setElectronicSign(true);
                    setSignImg(true);
                  } else if (newInputValue === "Without Electronically Signed") {
                    setElectronicSign(true);
                    setSignImg(false);
                  } else if (newInputValue === "Signature Img") {
                    setElectronicSign(false);
                    setSignImg(true);
                  }
                }}
              />
            </div>

            <div className="w-full sm:w-auto">
              <AddButton
                label="Download PDF"
                id="quotation_view_generatepdf"
                onClick={() =>
                  generatePDFInward({
                    letterHead,
                    jobDetails,
                    inputStates,
                    testRows,
                    officeFormData,
                    setLoading,
                    userList,
                    electronicSign,
                    signImg,
                    isCSRF,
                  })
                }
              />
            </div>

          </div>
        )}

        <div className="w-full lg:w-auto">
          <AddButton
            label="Back"
            id="quotation_view_back"
            variant="cancel"
            onClick={() => window.history.back()}
          />
        </div>
      </div>

      <h1 className="text-center font-bold mb-5 text-[2rem]">
        Test Request Form
      </h1>

      {/* === Test Request Form Table === */}
      <table className="w-full border border-black border-collapse mb-8">
        <tbody>
          {/* Customer and Other Details */}
          <tr>
            <td colSpan="2" className="border border-black font-semibold p-1">
              Customer Details
            </td>
            <td colSpan="2" className="border border-black font-semibold p-1">
              Other Details
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1">Name of Customer:</td>
            <td className="border border-black p-1">
              {inputStates?.customerName}
            </td>
            <td className="border border-black p-1">Report Dispatch Mode:</td>
            <td className="border border-black p-1">
              {DispatchModeOptions.find(option => option.id == inputStates?.dispatchMode)?.label || ""}
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1">
              Office Add, Email ID and Phone/Fax:
            </td>
            <td className="border border-black p-1">{inputStates?.address}</td>
            <td className="border border-black p-1">GSTIN:</td>
            <td className="border border-black p-1">{jobDetails?.gstNumber}</td>
          </tr>

          <tr>
            <td className="border border-black p-1">
              Name of Principal Client:
            </td>
            <td className="border border-black p-1">
              {inputStates?.principleClient}
            </td>
            <td className="border border-black p-1">PAN:</td>
            <td className="border border-black p-1">{jobDetails?.panNo}</td>
          </tr>

          <tr>
            <td className="border border-black p-1">Contact Person:</td>
            <td colSpan="3" className="border border-black p-1">
              {inputStates?.contactPerson}
            </td>
          </tr>

          {/* Sample Details */}
          <tr>
            <td
              colSpan="4"
              className="border border-black font-semibold p-1 bg-gray-100"
            >
              Sample Details
            </td>
          </tr>

          <tr>
            <td colSpan="2" className="border border-black p-1">
              <strong>Name of Work:</strong> {jobDetails?.projectName}
            </td>
            <td colSpan="2" className="border border-black p-1">
              <strong>Lab Ref No.:</strong>
              {inputStates?.labReferenceNo || ""}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ====== call common testRow component ====== */}
      <InwardTestTableCommom testRows={testRows} isCSRF={isCSRF} />

      <table className="w-full border border-black border-collapse text-xs mt-2">
        <tbody>
          <tr>
            <td colSpan={6} className="border border-black p-1 font-bold">
            Note: Standard Retention period of test sample at our permanent facility is 30 days according to Quality Manual (Clause no 7.4.1) 
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-semibold">
              Extra Retention Period Required
            </td>
            <td
              colSpan={officeFormData?.retentionFrequency ? 2 : 5}
              className="border border-black p-1 text-center"
            >
              {officeFormData?.retentionperiod}
            </td>
            {officeFormData?.retentionFrequency && (
              <>
                <td className="border border-black p-1 font-semibold">
                  Extra Retention Required For:
                </td>
                <td className="border border-black p-1 text-center">
                  {officeFormData?.retentionFrequency}
                </td>
              </>
            )}
          </tr>
          <tr>
            <td className="border border-black p-1 font-semibold">
              Statement of confirmity Required
            </td>
            <td colSpan={2} className="border border-black p-1 text-center">
              {officeFormData?.conformityStatement}
            </td>
            <td
              className="border text-center align-bottom  border-black p-1"
              rowSpan={2}
              colSpan={3}
            >
              <br/>
              <br/>
              <br/>
              Customer Signature with name
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1" colSpan={3}>
              Remarks: {officeFormData?.remarks}
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1 text-center" colSpan={6}>
              For Office Use Only
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1 font-bold" colSpan={6}>
              Review of Test Request
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1">Sample Adequacy</td>
            <td colSpan={2} className="border border-black p-1 text-center">
              {officeFormData?.sampleAdequacy}
            </td>
            <td className="border border-black p-1">Test Within NABL Scope</td>
            <td className="border border-black p-1 text-center" colSpan={2}>
              {officeFormData?.nablScope}
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1">Test Person Available</td>
            <td colSpan={2} className="border border-black p-1 text-center">
              {officeFormData?.testPersonstatus}
            </td>
            <td className="border border-black p-1">Source Adequacy</td>
            <td className="border border-black p-1 text-center" colSpan={2}>
              {officeFormData?.sourceAdequacy}
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1" colSpan={6}>
              Whether the lab has understood/fulfilled the requirements of the
              Customer - {officeFormData?.weatherLab}
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1">Condition Of Sample</td>
            <td colSpan={2} className="border border-black p-1 text-center">
              {officeFormData?.sampleCondition}
            </td>
            <td
              className="border text-center align-bottom border-black p-1"
              colSpan={3}
            >
              {(() => {
                const u = userList?.find(x => x.id == inputStates?.createdBy);
                return u ? (
                  <div className="w-full flex flex-col items-center justify-center font-bold">
                    <div className="h-[60px]">
                    {u.usersign && signImg && (
                      <img
                        src={u.usersign}
                        alt="sign"
                        className="h-10 object-contain mx-auto"
                      />
                    )}
                    </div>
                    <div>
                    Sample Inward By  {electronicSign && <span>- {u.employeeName}</span>}
                    </div>
                  </div>
                ) : "-";
              })()}
            </td>
          </tr>

          <tr>
            <td className="border border-black p-1 font-semibold" colSpan={6}>
              Instruction from Customers <small>(If Any) - </small>
              {officeFormData?.customerRequirement}
            </td>
          </tr>

          <tr>
            <td
              className="border border-black p-1 font-semibold align-top text-center"
              colSpan={3}
            >
              {(() => {
                const u = userList?.find(x => x.id == inputStates?.createdBy);
                return u ? (
                  <div className="w-full flex flex-col items-center justify-center font-bold">
                    <div className="h-[60px]">
                    {u.usersign && signImg && (
                      <img
                        src={u.usersign}
                        alt="sign"
                        className="h-10 object-contain mx-auto"
                      />
                    )}
                    </div>
                    <div>
                      Test Contract Verified By
                      {electronicSign && <span> - {u.employeeName}</span>}
                    </div>
                  </div>
                ) : "-";
              })()}
            </td>
            <td
              className="border border-black p-1 font-semibold align-top text-center"
              colSpan={3}
            >
              {(() => {
                const u = userList?.find(x => x.id == officeFormData?.technicalManager);
                return u ? (
                  <div className="w-full flex flex-col items-center justify-center font-bold">
                    <div className="h-[60px]">
                    {u.usersign && signImg && signImg && (
                      <img
                        src={u.usersign}
                        alt="sign"
                        className="h-10 object-contain mx-auto"
                      />
                    )}
                    </div>
                    <div>
                    Technical Manager
                    {electronicSign && <span> - {u.employeeName}</span>}
                    </div>
                  </div>
                ) : "-";
              })()}
            </td>
          </tr>
        </tbody>
      </table>

      <LoaderModal loading={loading} />
    </div>
  );
};

export default InwardView;